import { Component, Input, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatMenu } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { MatChipInputEvent } from '@angular/material/chips';

export const CRITERIA_LIST = [
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' },
  { value: 'isEqual', label: 'Is equal' },
  { value: 'isNotEqual', label: 'Is not equal' },
  { value: 'containsValue', label: 'Contains value' },
];

export const DATE_CRITERIA_LIST = [
  { value: 'dateFrom', label: 'Date from' },
  { value: 'dateTo', label: 'Date to' },
];

export const CHECKBOX_CRITERIA_LIST = [{ value: 'containedInList', label: 'Contained in list' }];

export const TAG_CRITERIUM = { value: 'containedTagInList', label: 'Contained tag in list' };

export const MULTI_SELECT_CRITERIUM = { value: 'containedInList', label: 'Contained in list' };

export const CRITERIA_FUNCTIONS = {
  isEmpty(value: any) {
    return value === null || value === '';
  },
  isNotEmpty(value: any) {
    return value !== null && value !== '';
  },
  isEqual(value: any, filteredValue: any) {
    if (!value || !filteredValue) {
      return;
    }
    return value.toString().toLowerCase() === filteredValue.toString().toLowerCase();
  },
  isNotEqual(value: any, filteredValue: any) {
    if (!value || !filteredValue) {
      return;
    }
    return value.toString().toLowerCase() !== filteredValue.toString().toLowerCase();
  },
  containsValue(value: any, filteredValue: any) {
    if (value != null && filteredValue != null) {
      return value.toString().toLowerCase().includes(filteredValue.toString().toLowerCase());
    } else {
      return false;
    }
  },
  dateFrom(value: any, selectedValue: any) {
    return Date.parse(selectedValue) <= Date.parse(value);
  },
  dateTo(value: any, selectedValue: any) {
    return Date.parse(selectedValue) >= Date.parse(value);
  },
  containedInList(value: any, filteredValueList: any) {
    if (!filteredValueList) {
      return;
    }
    let contained = false;
    for (const listItem of filteredValueList) {
      const listValue = listItem != null && typeof listItem === 'object' ? listItem.name : listItem;
      if (listValue === value) {
        contained = true;
      }
    }
    return contained;
  },
  containedTagInList(value: any, filteredValueList: any) {
    if (!filteredValueList) {
      return;
    }
    let contained = false;
    for (const listItem of filteredValueList) {
      if (value.includes(listItem)) {
        contained = true;
      }
    }
    return contained;
  },
};

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

const MULTI_SELECT_FILTER_WIDTH = 300;

@Component({
  selector: 'app-filter-menu',
  templateUrl: './filter-menu.component.html',
  styleUrls: ['./filter-menu.component.scss'],
  exportAs: 'columnFilterComponent',
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class FilterMenuComponent {
  @ViewChild('filterMenu', { static: true }) filterMenu!: MatMenu;
  @ViewChild('dateFilterMenu', { static: true }) dateFilterMenu!: MatMenu;
  @ViewChild('checkboxFilterMenu', { static: true }) checkboxFilterMenu!: MatMenu;
  @ViewChild('tagFilterMenu', { static: true }) tagFilterMenu!: MatMenu;
  @ViewChild('multiSelectFilterMenu', { static: true }) multiSelectFilterMenu!: MatMenu;

  @Input() dataSource!: MatTableDataSource<any> | PaginationDataSource<any>;

  public criteriaList = CRITERIA_LIST;
  public dateCriteriaList = DATE_CRITERIA_LIST;
  public checkboxCriteriaList = CHECKBOX_CRITERIA_LIST;
  public tagCriterium = TAG_CRITERIUM;
  public multiSelectCriterium = MULTI_SELECT_CRITERIUM;
  private filterMethods = CRITERIA_FUNCTIONS;
  public searchValue: any = {};
  public searchCriterion: any = {};
  selectedCategories: any = {};
  selectedTags: any = {};

  formControlsMap: Record<string, FormControl<string[] | null>> = {};
  selectAllValue = 'SELECT_ALL';
  multiSelectFilterWidth = MULTI_SELECT_FILTER_WIDTH;
  compareWith: (item1: string, item2: string) => boolean = (item1: string, item2: string) => item1 === item2;

  public filterMode: Record<string, 'AND' | 'OR'> = {};
  public textFilterValues: Record<string, string[]> = {};
  public showChipInput: Record<string, boolean> = {};
  public searchTerm: Record<string, string> = {};

  constructor() {}

  getFormControl(caller: string): FormControl<string[] | null> {
    if (this.formControlsMap[caller] != null) {
      return this.formControlsMap[caller];
    }
    this.formControlsMap[caller] = new FormControl<string[]>([]);
    return this.formControlsMap[caller];
  }

  toggleFilterMode(caller: string): void {
    this.filterMode[caller] = this.filterMode[caller] === 'AND' ? 'OR' : 'AND';
  }

  applyColumnFilter(caller?: string) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller) {
        const mode = this.filterMode[caller] ?? 'OR';
        this.dataSource.addFilterOptions(caller, this.getTextFilterValues(caller), mode);
      }
      return;
    }

    if (caller) {
      this.searchCriterion[caller] = this.criteriaList[4].value;
      this.searchValue[caller] = this.getTextFilterValues(caller)[0];
    }
    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };
    this.dataSource.filter = searchFilter;
  }

  applyDateColumnFilter(caller: string) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller && this.searchValue[caller]) {
        this.dataSource.addFilterOptions(caller, [this.searchValue[caller]]);
      }
      return;
    }

    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };

    this.dataSource.filter = searchFilter;
  }

  applyTagColumnFilter(caller: string, targetList: { name: string; value: string }[]) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller && this.searchValue[caller] && this.searchValue[caller][caller]) {
        const mode = this.filterMode[caller] ?? 'OR';
        this.dataSource.addFilterOptions(
          caller,
          this.searchValue[caller][caller].map((x: { name: string; value: string }) => x.value),
          mode
        );
      }

      return;
    }

    this.searchCriterion[caller] = this.tagCriterium.value;
    this.searchValue[caller] = targetList;
    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };
    this.dataSource.filter = searchFilter;
  }

  applyMultiSelectColumnFilter(caller: string) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller && this.searchValue[caller]) {
        const mode = this.filterMode[caller] ?? 'OR';
        this.dataSource.addFilterOptions(caller, this.searchValue[caller], mode);
      }

      return;
    }

    this.searchCriterion[caller] = this.multiSelectCriterium.value;
    this.searchValue[caller] = this.formControlsMap[caller]?.value?.filter(_ => this.isNotSelectAllValue(_));
    if (this.searchValue[caller]?.length === 0) {
      this.searchValue[caller] = undefined;
    }
    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };
    this.dataSource.filter = searchFilter;
  }

  applyCheckboxColumnFilter(caller: string, targetList: string[]) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller && this.searchValue[caller] && this.searchValue[caller][caller]) {
        const mode = this.filterMode[caller] ?? 'OR';
        this.dataSource.addFilterOptions(
          caller,
          this.searchValue[caller][caller].map((x: { name: string; value: string }) => x.value),
          mode
        );
      }

      return;
    }

    this.searchCriterion[caller] = this.checkboxCriteriaList[0].value;
    this.searchValue[caller] = targetList;
    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };
    this.dataSource.filter = searchFilter;
  }

  clearColumn(columnKey: string): void {
    delete this.searchValue[columnKey];
    delete this.searchCriterion[columnKey];
    this.selectedCategories[columnKey] = undefined;
    this.selectedTags[columnKey] = undefined;
    this.filterMode[columnKey] = 'OR';
    this.textFilterValues[columnKey] = [];
    this.searchTerm[columnKey] = '';
    this.formControlsMap[columnKey]?.setValue([]);

    if (this.dataSource instanceof PaginationDataSource) {
      this.applyColumnFilter(columnKey);
    } else {
      this.applyColumnFilter();
    }
  }

  setSelected(checked: boolean, category: { name: string; value: any }, targetList: any, caller: string) {
    if (checked) {
      if (!targetList[caller]) {
        targetList[caller] = [];
      }
      targetList[caller].push(category);
    } else {
      const index = targetList[caller].indexOf(category);
      targetList[caller].splice(index, 1);
      if (targetList[caller].length === 0) {
        targetList[caller] = undefined;
      }
    }
    this.searchValue[caller] = targetList;
  }

  isSelected(category: { name: string; value: any }, targetList: { name: string; value: any }[]) {
    if (targetList && targetList.length > 0) {
      return targetList.some(x => x.name == category.name);
    }
  }

  multiSelectValueChanged(value: string | string[], caller: string) {
    this.searchValue[caller] = value;
  }

  preventEventBubbling(event: Event): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  private isNotSelectAllValue(item: string): boolean {
    return !this.compareWith(item, this.selectAllValue);
  }

  addChipValue(caller: string, event: MatChipInputEvent): void {
    const value = event.value.trim();
    if (value) {
      this.getTextFilterValues(caller).push(value);
    }
    event.chipInput?.clear();
  }

  addChipValueAndHideInput(caller: string, event: MatChipInputEvent): void {
    this.addChipValue(caller, event);
    this.showChipInput[caller] = false;
  }

  getTextFilterValues(caller: string): string[] {
    if (!this.textFilterValues[caller]) {
      this.textFilterValues[caller] = [];
    }
    return this.textFilterValues[caller];
  }

  removeTextFilterValue(caller: string, index: number): void {
    this.textFilterValues[caller]?.splice(index, 1);
  }

  getFilteredOptions(caller: string, options: { name: string; value: any }[]): { name: string; value: any }[] {
    const term = this.searchTerm[caller]?.toLowerCase().trim();
    if (!term) {
      return options;
    }
    return options.filter(o => o.name.toLowerCase().includes(term));
  }

  getSelectedOptionCount(caller: string, targetList: Record<string, { name: string; value: any }[]>): number {
    return targetList[caller]?.length ?? 0;
  }

  isAllOptionsSelected(caller: string, options: { name: string; value: any }[], targetList: Record<string, { name: string; value: any }[]>): boolean {
    const selected = targetList[caller]?.length ?? 0;
    return selected > 0 && selected === options.length;
  }

  isOptionsIndeterminate(caller: string, options: { name: string; value: any }[], targetList: Record<string, { name: string; value: any }[]>): boolean {
    const selected = targetList[caller]?.length ?? 0;
    return selected > 0 && selected < options.length;
  }

  toggleSelectAllOptions(checked: boolean, options: { name: string; value: any }[], targetList: any, caller: string): void {
    targetList[caller] = checked ? [...options] : undefined;
    this.searchValue[caller] = targetList;
  }
}
